import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import {
    Grid,
    Radio,
    RadioGroup,
    FormControlLabel,
    Button,
    Box,
    CircularProgress,
    Stack,
    TextField,
    Collapse,
    Typography,
    Checkbox,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Popover,
    IconButton,
    Modal,
    FormControl,
    FormLabel,
    FormHelperText,
    Tooltip,
} from '@mui/material';
import AlertDialog from '../../AlertDialog/AlertDialog';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { RootState } from '../../../reducers';
import { getPastPresentHistory, gotoPreviousMenu } from '../../../store/assessment.slice';
import { copyObject, highlightStyle, preventTextInput } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { fetchTobaccoAndDrugAssessmentById, saveTobaccoAndDrugAssessment, setTobaccoAndDrugAssessment } from '../../../store/hra.slice';
import { AlcoholProblems, AuditAnswers, DrinkContainingAlcoholFrequencies, NumberOfDrinks, YesNoAnswers } from '../../../utils/assessments';
import { TobaccoAssessment } from '../../../Models/HRA/TobaccoAssessment.model';
import { TobaccoAndDrugAssessment } from '../../../Models/HRA/TobaccoAndDrugAssessment.model';
import { DrugAssessment } from '../../../Models/HRA/DrugAssessment.model';
import { GenderEnum } from '../../../Enum/GenderEnum';
import classes from '../Styles';
import { AuditAssessment, EducationOnDrinking } from '../../../Models/HRA/AuditAssessment.model';
import { AnswerOptionsEnum } from '../../../Enum/AnswerOptionsEnum';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import SafetyPlanPromptModal from '../../SafetyPlanPromptModal/SafetyPlanPromptModal';
import { openSafetyPlanModal, setShowSafetyPlanModal } from '../../../store/behaviorOrSafetyPlanSlice.slice';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_PHQ9_SIHI_DAST.pdf';

const educationOnDrinkingFormSchema = Yup.object().shape({
    CompletedStatus: Yup.mixed().nullable().required('Required.'),
    ReasonForNotApplicable: Yup.string().when('CompletedStatus', {
        is: String(AnswerOptionsEnum.NotApplicable),
        then: Yup.string().nullable().required('Reason is required.'),
    }),
});

const TobaccoAndDrug: React.FC<{ memberId: string }> = ({ memberId }) => {
    const [anchorInfoEl, setAnchorInfoEl] = React.useState<HTMLButtonElement | null>(null);
    const [showAuditInfo, setShowAuditInfo] = React.useState<boolean>(false);
    const [showDrugInfo, setShowDrugInfo] = React.useState<boolean>(false);
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { showSafetyPlanModal } = useSelector((state: RootState) => state.safetyPlanSlice);
    const [newEducationOnDrinking, setNewEducationOnDrinking] = useState<EducationOnDrinking>(new EducationOnDrinking());
    const [showEducationOnDrinking, setShowEducationOnDrinking] = React.useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { memberBasicInfo } = useSelector((state: RootState) => state.memberDetail);
    const { tobaccoAndDrug, isFetching, isSaving: isSavingHra } = useSelector((state: RootState) => state.hra);
    const { engagementDraft, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isSaving = isSavingHra || isSavingEngagementDraft;
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;

    useEffect(() => {
        if (Boolean(engagementDraft.TobaccoAndDrugAssessmentId)) {
            dispatch(fetchTobaccoAndDrugAssessmentById(engagementDraft.TobaccoAndDrugAssessmentId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, memberId]);

    useEffect(() => {
        dispatch(getPastPresentHistory(memberId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, memberId]);

    useEffect(() => {
        if (!Boolean(anchorInfoEl)) {
            setTimeout(() => {
                setShowAuditInfo(false);
                setShowDrugInfo(false);
            }, 600);
        }
    }, [anchorInfoEl]);

    const tobaccoAndDrugIsComplete = (assessment: TobaccoAndDrugAssessment | null | undefined): boolean => {
        if (!assessment) return false;
        // check if required fields have a value
        const requiredFields = [
            assessment.TobaccoAssessment?.HasTobaccoUse,
            assessment.HasFiveOrMoreDrinksPerDayInPastYear,
            assessment.DrugAssessment?.HasUsageInPastYear,
        ];
        // false is a completed answer (No / None). Only unanswered fields are incomplete.
        return requiredFields.every((field) => field === true || field === false);
    };

    const isSafetyPlanTriggered = (): boolean => {
        return tobaccoAndDrug?.HasFiveOrMoreDrinksPerDayInPastYear === true || tobaccoAndDrug?.DrugAssessment?.HasUsageInPastYear === true;
    };

    const proceedToSaveFlow = () => {
        if (tobaccoAndDrug?.AuditAssessment?.Score > 0 && tobaccoAndDrug?.AuditAssessment?.Score <= 7) {
            setShowEducationOnDrinking(true);
            setNewEducationOnDrinking(tobaccoAndDrug.AuditAssessment?.EducationOnDrinking || new EducationOnDrinking());
        } else {
            setShowSaveAlert(true);
        }
    };

    const handleOnClickSave = () => {
        if (isSafetyPlanTriggered()) {
            dispatch(openSafetyPlanModal());
            return;
        }
        proceedToSaveFlow();
    };

    const handleSafetyPlanModalComplete = () => {
        dispatch(setShowSafetyPlanModal(false));
        proceedToSaveFlow();
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        const assessmentToSave = {
            ...tobaccoAndDrug,
            ...(isWithoutComplete ? { Status: StatusEnum.Completed } : {}),
            MemberId: memberId,
        };
        dispatch(saveTobaccoAndDrugAssessment(assessmentToSave, true));
    };

    const handleOnChangeMainAnswer = (answer: any, propName: string) => {
        let data = copyObject(tobaccoAndDrug);
        data[propName] = answer;
        const aiPropertyName = `IsAI${propName}`;
        if (data.hasOwnProperty(aiPropertyName)) {
            data[aiPropertyName] = false;
        }
        data.MemberId = memberId;

        dispatch(setTobaccoAndDrugAssessment(data));
    };

    const handleOnChangeTobaccoAnswer = (answer: any, propName: string) => {
        let data = copyObject(tobaccoAndDrug);
        if (data.TobaccoAssessment === null) {
            data.TobaccoAssessment = new TobaccoAssessment();
        }

        data.TobaccoAssessment[propName] = answer;
        const aiPropertyName = `IsAI${propName}`;
        if (data.TobaccoAssessment.hasOwnProperty(aiPropertyName)) {
            data.TobaccoAssessment[aiPropertyName] = false;
        }
        // Field name typo vs AI flag name (AverageTobocco* vs AverageTobacco*)
        if (propName === 'AverageToboccoUsagePerDay' && Object.prototype.hasOwnProperty.call(data.TobaccoAssessment, 'IsAIAverageTobaccoUsagePerDay')) {
            data.TobaccoAssessment.IsAIAverageTobaccoUsagePerDay = false;
        }
        if (propName === 'HasTobaccoUse' && answer === false) {
            data.TobaccoAssessment.IsAIHasCigrettesUse = false;
            data.TobaccoAssessment.IsAIChewingTobacco = false;
            data.TobaccoAssessment.IsAIVaping = false;
            data.TobaccoAssessment.IsAITobaccoUseInYears = false;
            data.TobaccoAssessment.IsAIInterestedInQuitting = false;
            data.TobaccoAssessment.IsAIAverageTobaccoUsagePerDay = false;
        }
        data.MemberId = memberId;

        dispatch(setTobaccoAndDrugAssessment(data));
    };

    const handleOnChangeDrugAnswer = (answer: any, propName: string) => {
        let data = copyObject(tobaccoAndDrug);
        if (data.DrugAssessment === null) {
            data.DrugAssessment = new DrugAssessment();
        }

        data.DrugAssessment[propName] = answer;
        const aiPropertyName = `IsAI${propName}`;
        if (data.DrugAssessment.hasOwnProperty(aiPropertyName)) {
            data.DrugAssessment[aiPropertyName] = false;
        }
        data.MemberId = memberId;

        dispatch(setTobaccoAndDrugAssessment(data));
    };

    const handleOnChangeAuditAnswer = (answer: any, propName: string) => {
        let data = copyObject(tobaccoAndDrug) as TobaccoAndDrugAssessment;
        if (data.AuditAssessment === null) {
            data.AuditAssessment = new AuditAssessment();
        }

        data.AuditAssessment[propName] = answer;
        const aiPropertyName = `IsAI${propName}`;
        if (data.AuditAssessment.hasOwnProperty(aiPropertyName)) {
            data.AuditAssessment[aiPropertyName] = false;
        }
        data.MemberId = memberId;
        dispatch(setTobaccoAndDrugAssessment(data));
    };

    const prepareAuditQuestions = () => {
        return [
            {
                name: 'DrinkContainingAlcoholFrequency',
                question: AssessmentQuestions.SA_DrinkContainingAlcoholFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.DrinkContainingAlcoholFrequency,
                options: DrinkContainingAlcoholFrequencies,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIDrinkContainingAlcoholFrequency,
            },
            {
                name: 'DrinkContainingAlcoholPerDayFrequency',
                question: AssessmentQuestions.SA_DrinkContainingAlcoholPerDayFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.DrinkContainingAlcoholPerDayFrequency,
                options: NumberOfDrinks,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIDrinkContainingAlcoholPerDayFrequency,
            },
            {
                name: 'SixOrMoreDrinksOnOneOccasionFrequency',
                question: AssessmentQuestions.SA_SixOrMoreDrinksOnOneOccasionFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.SixOrMoreDrinksOnOneOccasionFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAISixOrMoreDrinksOnOneOccasionFrequency,
            },
            {
                name: 'NotAbleToStopOnceStartedFrequency',
                question: AssessmentQuestions.SA_NotAbleToStopOnceStartedFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.NotAbleToStopOnceStartedFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAINotAbleToStopOnceStartedFrequency,
            },
            {
                name: 'FailedToDoBecauseOfDrinkingFrequency',
                question: AssessmentQuestions.SA_FailedToDoBecauseOfDrinkingFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.FailedToDoBecauseOfDrinkingFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIFailedToDoBecauseOfDrinkingFrequency,
            },
            {
                name: 'LastYearNeededDrinkFirstThingFrequency',
                question: AssessmentQuestions.SA_LastYearNeededDrinkFirstThingFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.LastYearNeededDrinkFirstThingFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAILastYearNeededDrinkFirstThingFrequency,
            },
            {
                name: 'FeltGuiltyOrRemorsefulForDrinkingFrequency',
                question: AssessmentQuestions.SA_FeltGuiltyOrRemorsefulForDrinkingFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.FeltGuiltyOrRemorsefulForDrinkingFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIFeltGuiltyOrRemorsefulForDrinkingFrequency,
            },
            {
                name: 'UnableToRememberWhatHappenedFrequency',
                question: AssessmentQuestions.SA_UnableToRememberWhatHappenedFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.UnableToRememberWhatHappenedFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIUnableToRememberWhatHappenedFrequency,
            },
            {
                name: 'InjuredAfterDrinkingFrequency',
                question: AssessmentQuestions.SA_InjuredAfterDrinkingFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.InjuredAfterDrinkingFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIInjuredAfterDrinkingFrequency,
            },
            {
                name: 'OthersConcernedAboutDrinkingFrequency',
                question: AssessmentQuestions.SA_OthersConcernedAboutDrinkingFrequency,
                answer: tobaccoAndDrug.AuditAssessment?.OthersConcernedAboutDrinkingFrequency,
                options: AuditAnswers,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIOthersConcernedAboutDrinkingFrequency,
            },
            {
                name: 'EverBeenInTreatmentForAlcoholProblem',
                question: AssessmentQuestions.SA_EverBeenInTreatmentForAlcoholProblem,
                answer: tobaccoAndDrug.AuditAssessment?.EverBeenInTreatmentForAlcoholProblem,
                options: AlcoholProblems,
                isAI: tobaccoAndDrug.AuditAssessment?.IsAIEverBeenInTreatmentForAlcoholProblem,
            },
        ];
    };

    const preparePast12yearsQuestions = () => {
        return [
            {
                question: AssessmentQuestions.SA_HasUsedOtherThanMedicalReason,
                answer: tobaccoAndDrug.DrugAssessment?.HasUsedOtherThanMedicalReason,
                name: 'HasUsedOtherThanMedicalReason',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIHasUsedOtherThanMedicalReason,
            },
            {
                question: AssessmentQuestions.SA_HasAbusedMoreThanOne,
                answer: tobaccoAndDrug.DrugAssessment?.HasAbusedMoreThanOne,
                name: 'HasAbusedMoreThanOne',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIHasAbusedMoreThanOne,
            },
            {
                question: AssessmentQuestions.SA_UnableToStop,
                answer: tobaccoAndDrug.DrugAssessment?.UnableToStop,
                name: 'UnableToStop',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIUnableToStop,
            },
            {
                question: AssessmentQuestions.SA_HasBlackoutsOrFlashback,
                answer: tobaccoAndDrug.DrugAssessment?.HasBlackoutsOrFlashback,
                name: 'HasBlackoutsOrFlashback',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIHasBlackoutsOrFlashback,
            },
            {
                question: AssessmentQuestions.SA_FeltGuiltyAbout,
                answer: tobaccoAndDrug.DrugAssessment?.FeltGuiltyAbout,
                name: 'FeltGuiltyAbout',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIFeltGuiltyAbout,
            },
            {
                question: AssessmentQuestions.SA_FamilyComplained,
                answer: tobaccoAndDrug.DrugAssessment?.FamilyComplained,
                name: 'FamilyComplained',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIFamilyComplained,
            },
            {
                question: AssessmentQuestions.SA_NeglectedFamily,
                answer: tobaccoAndDrug.DrugAssessment?.NeglectedFamily,
                name: 'NeglectedFamily',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAINeglectedFamily,
            },
            {
                question: AssessmentQuestions.SA_EngagedInIllegalActivityToObtainDrug,
                answer: tobaccoAndDrug.DrugAssessment?.EngagedInIllegalActivityToObtainDrug,
                name: 'EngagedInIllegalActivityToObtainDrug',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIEngagedInIllegalActivityToObtainDrug,
            },
            {
                question: AssessmentQuestions.SA_IsExperiencedWithdrawalSymptoms,
                answer: tobaccoAndDrug.DrugAssessment?.IsExperiencedWithdrawalSymptoms,
                name: 'IsExperiencedWithdrawalSymptoms',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIIsExperiencedWithdrawalSymptoms,
            },
            {
                question: AssessmentQuestions.SA_HasMedicalProblemsDueToDrugUsage,
                answer: tobaccoAndDrug.DrugAssessment?.HasMedicalProblemsDueToDrugUsage,
                name: 'HasMedicalProblemsDueToDrugUsage',
                isAI: tobaccoAndDrug.DrugAssessment?.IsAIHasMedicalProblemsDueToDrugUsage,
            },
        ];
    };

    const handleOnSubmitEducationOnDrink = (values: EducationOnDrinking) => {
        let data = copyObject(tobaccoAndDrug) as TobaccoAndDrugAssessment;
        data.AuditAssessment.EducationOnDrinking = values;
        data.MemberId = memberId;
        dispatch(setTobaccoAndDrugAssessment(data));
        setShowSaveAlert(true);
        setShowEducationOnDrinking(false);
    };

    const renderEducationOnDrinking = () => {
        const handleOnCloseModal = () => setShowEducationOnDrinking(false);

        return (
            <Modal open={showEducationOnDrinking} onClose={handleOnCloseModal}>
                <Formik
                    initialValues={newEducationOnDrinking}
                    enableReinitialize={true}
                    onSubmit={handleOnSubmitEducationOnDrink}
                    validationSchema={educationOnDrinkingFormSchema}
                >
                    {({ values, errors, handleChange, submitCount }: FormikProps<EducationOnDrinking>) => (
                        <Form noValidate>
                            <Box sx={[classes.mdModalPopupForm, classes.form]}>
                                <div className="mui-modal-header">
                                    <Typography variant="h6" component="h6">
                                        Education on drinking and harm reduction
                                    </Typography>
                                </div>
                                <div className="mui-modal-body">
                                    <Stack>
                                        <FormControl error={Boolean(submitCount) && Boolean(errors?.CompletedStatus)}>
                                            <FormLabel id="education-radio-buttons-group-label">Education completed</FormLabel>
                                            <RadioGroup
                                                row
                                                aria-labelledby="education-radio-buttons-group-label"
                                                value={values.CompletedStatus}
                                                name="CompletedStatus"
                                                onChange={handleChange}
                                            >
                                                <FormControlLabel value={AnswerOptionsEnum.Yes} control={<Radio />} label={AnswerOptionsEnum.YesDescription} />
                                                <FormControlLabel value={AnswerOptionsEnum.No} control={<Radio />} label={AnswerOptionsEnum.NoDescription} />
                                                <FormControlLabel
                                                    value={AnswerOptionsEnum.NotApplicable}
                                                    control={<Radio />}
                                                    label={AnswerOptionsEnum.NotApplicableDescription}
                                                />
                                            </RadioGroup>
                                            <FormHelperText>
                                                {Boolean(submitCount) && Boolean(errors?.CompletedStatus) ? errors?.CompletedStatus : ''}
                                            </FormHelperText>
                                        </FormControl>
                                        <Collapse in={String(values.CompletedStatus) === String(AnswerOptionsEnum.NotApplicable)} timeout="auto" unmountOnExit>
                                            <TextField
                                                fullWidth
                                                label="Reason"
                                                variant="outlined"
                                                size="small"
                                                multiline
                                                rows={5}
                                                value={values.ReasonForNotApplicable}
                                                name="ReasonForNotApplicable"
                                                onChange={handleChange}
                                                error={Boolean(submitCount) && Boolean(errors?.ReasonForNotApplicable)}
                                                helperText={Boolean(submitCount) ? errors?.ReasonForNotApplicable : ''}
                                            />
                                        </Collapse>
                                    </Stack>
                                </div>
                                <div className="mui-modal-footer">
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        size="small"
                                        disabled={isSavingHra}
                                        endIcon={isSavingHra ? <CircularProgress size={18} color="inherit" /> : null}
                                    >
                                        Save
                                    </Button>
                                    <Button size="small" type="button" onClick={handleOnCloseModal} variant="outlined" disabled={isSavingHra}>
                                        Cancel
                                    </Button>
                                </div>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Modal>
        );
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel" sx={{ pt: 4 }}>
            <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 2 }}>
                <Typography variant="h6">Tobacco and Drug Use</Typography>
                <Tooltip title="Tobacco and Drug Use Help Document">
                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                </Tooltip>
            </Stack>
            {isFetching ? (
                <Box display="flex" justifyContent="center" className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <React.Fragment>
                    <Grid className="content-form">
                        <div>
                            <Typography variant="h6" component="label">
                                Tobacco Use
                            </Typography>
                            {isBHDiagnostic && (
                                <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                    * Required for BH Diagnostic
                                </Typography>
                            )}
                            <hr />
                        </div>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ sm: 6, md: 6, lg: 8 }} display="flex" alignItems="center">
                                <FormLabel sx={{ mb: 2, ...(isBHDiagnostic ? highlightStyle : undefined) }} required={isBHDiagnostic}>
                                    {AssessmentQuestions.SA_THasTobaccoUse}
                                    {tobaccoAndDrug.TobaccoAssessment?.IsAIHasTobaccoUse && isAIIcon()}
                                </FormLabel>
                            </Grid>
                            <Grid>
                                <RadioGroup
                                    row
                                    name="HasTobaccoUse"
                                    value={String(tobaccoAndDrug.TobaccoAssessment?.HasTobaccoUse)}
                                    onChange={(e) => handleOnChangeTobaccoAnswer(e.target.value === 'true', e.target.name)}
                                >
                                    {YesNoAnswers.map((type, index) => {
                                        return <FormControlLabel key={`is-active-${index}`} value={type.Id} control={<Radio />} label={type.Name} />;
                                    })}
                                </RadioGroup>
                            </Grid>
                        </Grid>
                        {Boolean(tobaccoAndDrug.TobaccoAssessment) ? (
                            <Collapse in={tobaccoAndDrug.TobaccoAssessment?.HasTobaccoUse} timeout="auto" unmountOnExit>
                                <Grid container spacing={2}>
                                    <Grid size={0.5} />
                                    <Grid>
                                        <Grid container spacing={2}>
                                            <Grid size={{ sm: 6, md: 6, lg: 3 }} display="flex" alignItems="center">
                                                <Typography variant="subtitle1" component="label">
                                                    {AssessmentQuestions.SA_TCigarettes}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ sm: 6, md: 6, lg: 5 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            name="HasCigrettesUse"
                                                            checked={tobaccoAndDrug.TobaccoAssessment.HasCigrettesUse}
                                                            onChange={(e) => handleOnChangeTobaccoAnswer(e.target.checked, e.target.name)}
                                                        />
                                                    }
                                                    label={<Typography>Yes {tobaccoAndDrug.TobaccoAssessment.IsAIHasCigrettesUse && isAIIcon()}</Typography>}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid size={{ sm: 6, md: 6, lg: 3 }} display="flex" alignItems="center">
                                                <Typography variant="subtitle1" component="label">
                                                    {AssessmentQuestions.SA_TChewingTobacco}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ sm: 6, md: 6, lg: 5 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            name="IsChewingTobacco"
                                                            checked={tobaccoAndDrug.TobaccoAssessment.IsChewingTobacco}
                                                            onChange={(e) => handleOnChangeTobaccoAnswer(e.target.checked, e.target.name)}
                                                        />
                                                    }
                                                    label={<Typography>Yes {tobaccoAndDrug.TobaccoAssessment.IsAIChewingTobacco && isAIIcon()}</Typography>}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid size={{ sm: 6, md: 6, lg: 3 }} display="flex" alignItems="center">
                                                <Typography variant="subtitle1" component="label">
                                                    {AssessmentQuestions.SA_TVaping}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ sm: 6, md: 6, lg: 5 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            name="IsVaping"
                                                            checked={tobaccoAndDrug.TobaccoAssessment.IsVaping}
                                                            onChange={(e) => handleOnChangeTobaccoAnswer(e.target.checked, e.target.name)}
                                                        />
                                                    }
                                                    label={<Typography>Yes {tobaccoAndDrug.TobaccoAssessment.IsAIVaping && isAIIcon()}</Typography>}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Stack direction="row" spacing={2} alignItems="center" marginBottom={4}>
                                            <Typography variant="subtitle1" component="label">
                                                {AssessmentQuestions.SA_HowManyYears}
                                            </Typography>
                                            <TextField
                                                label="Years"
                                                variant="outlined"
                                                name="TobaccoUseInYears"
                                                className="hide-textinput-label"
                                                type="number"
                                                onKeyPress={preventTextInput}
                                                defaultValue={tobaccoAndDrug.TobaccoAssessment?.TobaccoUseInYears}
                                                inputProps={{ min: 0, max: 100 }}
                                                style={{ width: '120px' }}
                                                InputProps={{
                                                    endAdornment: tobaccoAndDrug.TobaccoAssessment.IsAITobaccoUseInYears && isAIIcon(),
                                                }}
                                                onBlur={(e) => handleOnChangeTobaccoAnswer(e.target.value, e.target.name)}
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={2} alignItems="center" marginBottom={1}>
                                            <Typography variant="subtitle1" component="label">
                                                {AssessmentQuestions.SA_AvgUsagePerDay}
                                            </Typography>
                                            <TextField
                                                variant="outlined"
                                                name="AverageToboccoUsagePerDay"
                                                className="hide-textinput-label"
                                                type="number"
                                                onKeyPress={preventTextInput}
                                                defaultValue={tobaccoAndDrug.TobaccoAssessment?.AverageToboccoUsagePerDay}
                                                placeholder="Enter quantity here"
                                                inputProps={{ min: 0, max: 100 }}
                                                style={{ width: '200px' }}
                                                onBlur={(e) => handleOnChangeTobaccoAnswer(e.target.value, e.target.name)}
                                                InputProps={{
                                                    endAdornment: tobaccoAndDrug.TobaccoAssessment.IsAIAverageTobaccoUsagePerDay && isAIIcon(),
                                                }}
                                            />
                                        </Stack>
                                        <Grid container spacing={2}>
                                            <Grid size={{ sm: 6, md: 6, lg: 12 }}>
                                                <Stack direction="row" spacing={2}>
                                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                                        <Typography variant="subtitle1" component="label" sx={{ pb: 2 }}>
                                                            {AssessmentQuestions.SA_InterestedInQuitting}
                                                            {tobaccoAndDrug.TobaccoAssessment.IsAIInterestedInQuitting && isAIIcon()}
                                                        </Typography>
                                                        <RadioGroup
                                                            row
                                                            name="IsInterestedInQuitting"
                                                            value={String(tobaccoAndDrug.TobaccoAssessment.IsInterestedInQuitting)}
                                                            onChange={(e) => handleOnChangeTobaccoAnswer(e.target.value === 'true', e.target.name)}
                                                        >
                                                            {YesNoAnswers.map((type, index) => {
                                                                return (
                                                                    <FormControlLabel
                                                                        key={`is-active-${index}`}
                                                                        value={type.Id}
                                                                        control={<Radio />}
                                                                        label={type.Name}
                                                                    />
                                                                );
                                                            })}
                                                        </RadioGroup>
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ sm: 6, md: 6, lg: 6 }}></Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        ) : null}

                        <div className="mt-4">
                            <Typography variant="h6" component="label">
                                Drinks Use
                            </Typography>
                            <hr />
                        </div>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ sm: 6, md: 6, lg: 8 }} display="flex" alignItems="center">
                                <FormLabel sx={{ mb: 2, ...(isBHDiagnostic ? highlightStyle : undefined) }} required={isBHDiagnostic}>
                                    {`How many times in the past year have you had ${
                                        memberBasicInfo.Gender === GenderEnum.MaleDescription ? '5' : '4'
                                    } or more drinks in a day?`}
                                    {tobaccoAndDrug.IsAIHasFiveOrMoreDrinksPerDayInPastYear && isAIIcon()}
                                    <IconButton
                                        onClick={(e) => {
                                            setShowAuditInfo(true);
                                            setAnchorInfoEl(e.currentTarget);
                                        }}
                                    >
                                        <InfoIcon />
                                    </IconButton>
                                </FormLabel>
                            </Grid>
                            <Grid>
                                <RadioGroup
                                    row
                                    name="HasFiveOrMoreDrinksPerDayInPastYear"
                                    value={String(tobaccoAndDrug.HasFiveOrMoreDrinksPerDayInPastYear)}
                                    onChange={(e) => handleOnChangeMainAnswer(e.target.value === 'true', e.target.name)}
                                >
                                    <FormControlLabel value="false" control={<Radio />} label="None" />
                                    <FormControlLabel value="true" control={<Radio />} label="1 or more" />
                                </RadioGroup>
                            </Grid>
                        </Grid>
                        <Collapse in={tobaccoAndDrug.HasFiveOrMoreDrinksPerDayInPastYear} timeout="auto" unmountOnExit>
                            <TableContainer component={Paper} sx={[{ ...classes.budgetFormTable }, { mt: 2 }]}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell colSpan={2}>
                                                <Typography variant="subtitle1" component="label">
                                                    <strong>AUDIT Questions</strong>
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {prepareAuditQuestions().map((item, index) => (
                                            <TableRow key={`drug-question-${index}`}>
                                                <TableCell align="center" style={{ verticalAlign: 'top' }}>
                                                    <Typography variant="subtitle1" component="label" paddingTop="2px">
                                                        {index + 1}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack>
                                                        <Typography variant="subtitle1" fontWeight="600" component="label">
                                                            {item.question}
                                                            {item.isAI && isAIIcon()}
                                                        </Typography>
                                                        <Stack direction="row" marginTop={1}>
                                                            {item.options.map((option, index) => (
                                                                <FormControlLabel
                                                                    key={`${option.Id}_${index}`}
                                                                    control={
                                                                        <Checkbox
                                                                            name={item.name}
                                                                            checked={Boolean(item.answer) && item.answer === option.Id}
                                                                            onChange={(e) =>
                                                                                handleOnChangeAuditAnswer(e.target.checked ? option.Id : null, e.target.name)
                                                                            }
                                                                            icon={<PanoramaFishEyeIcon />}
                                                                            checkedIcon={<RadioButtonCheckedIcon />}
                                                                        />
                                                                    }
                                                                    label={option.Name}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell component="th" scope="row" colSpan={2}>
                                                <Typography variant="subtitle1" component="label">
                                                    <strong>Score: {tobaccoAndDrug.AuditAssessment.Score}</strong>
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>

                        <Typography variant="h6" component="label" sx={{ mt: 4 }}>
                            Drug Use
                            <IconButton
                                onClick={(e) => {
                                    setShowDrugInfo(true);
                                    setAnchorInfoEl(e.currentTarget);
                                }}
                            >
                                <InfoIcon />
                            </IconButton>
                        </Typography>
                        <hr />

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ sm: 6, md: 6, lg: 8 }} display="flex" alignItems="center">
                                <FormLabel sx={{ mb: 2, ...(isBHDiagnostic ? highlightStyle : undefined) }} required={isBHDiagnostic}>
                                    {AssessmentQuestions.SA_DrugUseAssessment}
                                    {tobaccoAndDrug.DrugAssessment?.IsAIHasUsageInPastYear && isAIIcon()}
                                    <IconButton onClick={(e) => setAnchorInfoEl(e.currentTarget)}>
                                        <InfoIcon />
                                    </IconButton>
                                </FormLabel>
                            </Grid>
                            <Grid>
                                <RadioGroup
                                    row
                                    name="HasUsageInPastYear"
                                    value={String(tobaccoAndDrug.DrugAssessment?.HasUsageInPastYear)}
                                    onChange={(e) => handleOnChangeDrugAnswer(e.target.value === 'true', e.target.name)}
                                >
                                    <FormControlLabel value="false" control={<Radio />} label="None" />
                                    <FormControlLabel value="true" control={<Radio />} label="1 or more" />
                                </RadioGroup>
                            </Grid>
                        </Grid>

                        <Collapse in={tobaccoAndDrug.DrugAssessment?.HasUsageInPastYear} timeout="auto" unmountOnExit>
                            <TableContainer component={Paper} sx={[{ ...classes.budgetFormTable }, { mt: 2 }]}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography variant="subtitle1" component="label">
                                                    <strong>In the past 12 months...</strong>
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preparePast12yearsQuestions().map((item, index) => (
                                            <TableRow key={`drug-question-${index}`}>
                                                <TableCell align="center">
                                                    <Typography variant="subtitle1" component="label">
                                                        {index + 1}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="subtitle1" component="label">
                                                        {item.question}
                                                        {item.isAI && isAIIcon()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <FormControlLabel
                                                        control={
                                                            <Radio
                                                                name={item.name}
                                                                checked={Boolean(item.answer)}
                                                                onChange={(e) => handleOnChangeDrugAnswer(e.target.checked, e.target.name)}
                                                            />
                                                        }
                                                        label="Yes"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <FormControlLabel
                                                        control={
                                                            <Radio
                                                                name={item.name}
                                                                checked={item.answer === false}
                                                                onChange={(e) => handleOnChangeDrugAnswer(!e.target.checked, e.target.name)}
                                                            />
                                                        }
                                                        label="No"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell component="th" scope="row" colSpan={4}>
                                                <Typography variant="subtitle1" component="label">
                                                    <strong>Score: {tobaccoAndDrug.DrugAssessment.Score}</strong>
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end" className="content-footer">
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 3 }}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button type="submit" variant="contained" onClick={handleOnClickSave} endIcon={<ArrowForwardIosIcon />}>
                            Next
                        </Button>
                    </Stack>
                </React.Fragment>
            )}
            {renderEducationOnDrinking()}
            <SafetyPlanPromptModal
                open={showSafetyPlanModal}
                onCancel={() => dispatch(setShowSafetyPlanModal(false))}
                onComplete={handleSafetyPlanModalComplete}
            />
            {Boolean(
                tobaccoAndDrugIsComplete(tobaccoAndDrug) || (!isBHDiagnostic && engagementDraft.TobaccoAndDrugAssessmentId)
            ) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Tobacco and Drug Assessment"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    message="I confirm and acknowledge that Tobacco and Drug assessment is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Tobacco and Drug Assessment"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    renderBody={() =>
                        BHDiagnosticConfirmationBody({
                            assessmentName: 'Tobacco and Drug',
                            diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                        })
                    }
                />
            )}
            <Popover
                open={Boolean(anchorInfoEl)}
                anchorEl={anchorInfoEl}
                onClose={() => setAnchorInfoEl(null)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                PaperProps={{
                    style: { maxWidth: '600px' },
                }}
            >
                {showAuditInfo ? (
                    <Stack direction="column" spacing={1} sx={{ p: 3 }}>
                        <Typography variant="body1" component="label" fontWeight="bold">
                            Drinks
                        </Typography>
                        <Typography variant="subtitle1" component="label">
                            One drink equals a 12 oz beer, 5 oz wine or 1.5 oz liquor (one shot)
                        </Typography>
                    </Stack>
                ) : showDrugInfo ? (
                    <Stack direction="column" spacing={1} sx={{ p: 3 }}>
                        <Typography variant="body1" component="label" fontWeight="bold">
                            Drug Use
                        </Typography>
                        <Typography variant="subtitle1" component="label">
                            Drug abuse refers to use of prescribed or over the counter drugs in excess of directions and/or any non-medical use of drugs
                        </Typography>
                    </Stack>
                ) : (
                    <Stack direction="column" spacing={1} sx={{ p: 3 }}>
                        <Typography variant="body1" component="label" fontWeight="bold">
                            Drugs
                        </Typography>
                        <Typography variant="subtitle1" component="label">
                            Recreational drugs include methamphetamines (speed, crystal), cannabis (marijuana, pot), inhalants (paint thinner, aerosol, glue),
                            tranquilizers (Valium), barbiturates, cocaine, ecstasy, hallucinogens (LSD, mushrooms), or narcotics (heroin).
                        </Typography>
                    </Stack>
                )}
            </Popover>
        </Grid>
    );
};

export default TobaccoAndDrug;
